import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, Form, Modal, Spinner } from "react-bootstrap";
import { FaCalendarCheck, FaCheckCircle, FaCalendarAlt } from "react-icons/fa";
import api from "../api";
import "./ProjectFormsPanel.css";
import LinkifiedText from "./LinkifiedText";
import AvailabilityPicker from "./AvailabilityPicker";
import { buildAvailabilitySessions } from "../utils/availabilitySessions";
import {
  formatSouthAfricaDateTime,
  SOUTH_AFRICA_TIME_LABEL,
} from "../utils/projectFormDateTime";

const answersFromResponse = (response) =>
  Object.fromEntries((response?.answers || []).map((answer) => [String(answer.field), answer.value]));

/**
 * Strip any slot IDs from a saved response that no longer exist in the
 * current form definition. This prevents the "contains an invalid slot"
 * error when a form is edited (slots added/removed) after a student has
 * already submitted a response.
 */
const sanitiseAnswersForForm = (answers, form) => {
  const sanitised = { ...answers };
  for (const field of form.fields || []) {
    if (field.type !== "availability") continue;
    const validSlotIds = new Set((field.slots || []).map((slot) => String(slot._id)));
    const fieldId = String(field._id);
    if (Array.isArray(sanitised[fieldId])) {
      sanitised[fieldId] = sanitised[fieldId].filter((slotId) => validSlotIds.has(String(slotId)));
    }
  }
  return sanitised;
};

// Every availability question after the first is folded into the first
// one's combined session picker, so drop them from the question list.
const displayFieldsFor = (form) => {
  let seenAvailability = false;
  return (form.fields || []).filter((field) => {
    if (field.type !== "availability") return true;
    if (seenAvailability) return false;
    seenAvailability = true;
    return true;
  });
};

const ProjectFormsPanel = ({ projectId, visible }) => {
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [activeForm, setActiveForm] = useState(null);
  const [answers, setAnswers] = useState({});
  const [saving, setSaving] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [success, setSuccess] = useState("");
  const [calendarLoading, setCalendarLoading] = useState(false);

  const loadForms = async () => {
    if (!projectId) return;
    setLoading(true);
    setLoadError("");
    try {
      const response = await api.get(`/projects/${projectId}/forms`);
      setForms(response.data || []);
      return response.data || [];
    } catch (error) {
      setLoadError(error.response?.data?.error || "Availability forms could not be loaded.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (visible && projectId) loadForms();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, projectId]);

  const publishedForms = useMemo(
    () => forms.filter((form) => form.status === "published"),
    [forms]
  );

  const openForm = (form) => {
    setActiveForm(form);
    // Sanitise the saved response: strip any slot IDs that no longer exist
    // in the current form definition (e.g. slots were removed after the student
    // originally responded). Without this, re-submitting would fail with
    // "contains an invalid slot".
    const rawAnswers = answersFromResponse(form.myResponse);
    setAnswers(sanitiseAnswersForForm(rawAnswers, form));
    setSubmitError("");
    setSuccess("");
  };

  const setAnswer = (fieldId, value) =>
    setAnswers((current) => ({ ...current, [fieldId]: value }));

  const toggleArrayAnswer = (fieldId, value) => {
    const current = Array.isArray(answers[fieldId]) ? answers[fieldId] : [];
    setAnswer(
      fieldId,
      current.includes(value)
        ? current.filter((item) => item !== value)
        : [...current, value]
    );
  };

  const submit = async (event) => {
    event.preventDefault();
    setSaving(true);
    setSubmitError("");
    setSuccess("");
    try {
      const response = await api.post(`/projects/${projectId}/forms/${activeForm._id}/responses`, {
        answers: activeForm.fields.map((field) => ({
          field: field._id,
          value: answers[field._id] ?? (["multi_choice", "availability"].includes(field.type) ? [] : ""),
        })),
      });
      setSuccess("Your availability has been saved.");
      const freshForms = await loadForms();
      // Pick up the new places-left counts along with the saved response.
      const fresh = freshForms?.find((form) => form._id === activeForm._id);
      setActiveForm((current) => ({ ...(fresh || current), myResponse: response.data.response }));
    } catch (error) {
      setSubmitError(error.response?.data?.error || "Your response could not be saved.");
    } finally {
      setSaving(false);
    }
  };

  const downloadCalendar = async () => {
    if (!activeForm) return;
    setCalendarLoading(true);
    try {
      const response = await api.get(
        `/projects/${projectId}/forms/${activeForm._id}/responses/calendar.ics`,
        { responseType: "blob" }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "volunteer-schedule.ics");
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
    } catch (error) {
      setSubmitError("Failed to download calendar.");
    } finally {
      setCalendarLoading(false);
    }
  };

  if (loading) {
    return <div className="project-form-loading"><Spinner animation="border" size="sm" /> Loading availability…</div>;
  }
  if (loadError) return <Alert>{loadError}</Alert>;
  if (!publishedForms.length) return null;

  return (
    <section className="project-info-section project-forms-panel" aria-labelledby="project-forms-title">
      <h3 id="project-forms-title">Volunteer availability</h3>
      <div className="project-form-cards">
        {publishedForms.map((form) => (
          <article key={form._id} className="project-form-card">
            <div className="project-form-card__icon"><FaCalendarCheck aria-hidden="true" /></div>
            <div className="project-form-card__content">
              <h4>{form.title}</h4>
              {form.description && <p><LinkifiedText text={form.description} /></p>}
              <div className="project-form-card__meta">
                {form.closesAt && <span>Closes {formatSouthAfricaDateTime(form.closesAt, "dd MMM yyyy, HH:mm")} {SOUTH_AFRICA_TIME_LABEL}</span>}
                {(() => {
                  const { upcomingCount, selectedCount } = buildAvailabilitySessions(
                    form.fields,
                    answersFromResponse(form.myResponse),
                  );
                  if (!form.fields.some((field) => field.type === "availability")) return null;
                  return (
                    <span>
                      <FaCalendarAlt aria-hidden="true" /> {upcomingCount} upcoming session{upcomingCount === 1 ? "" : "s"}
                      {selectedCount ? ` · ${selectedCount} picked` : ""}
                    </span>
                  );
                })()}
                {form.myResponse && <span><FaCheckCircle aria-hidden="true" /> Response saved</span>}
              </div>
            </div>
            <Button type="button" onClick={() => openForm(form)}>
              {form.myResponse ? "Update availability" : "Choose availability"}
            </Button>
          </article>
        ))}
      </div>

      <Modal show={Boolean(activeForm)} onHide={() => !saving && setActiveForm(null)} size="lg" centered scrollable dialogClassName="project-form-modal">
        {activeForm && (
          <>
            <Modal.Header closeButton>
              <div>
                <Modal.Title>{activeForm.title}</Modal.Title>
                {activeForm.description && <p className="project-form-modal-description"><LinkifiedText text={activeForm.description} /></p>}
              </div>
            </Modal.Header>
            <Modal.Body>
              <Form id="availability-form" onSubmit={submit}>
                {submitError && <Alert variant="danger">{submitError}</Alert>}
                {success && <Alert variant="success">{success}</Alert>}

                {(activeForm.myResponse || success) && activeForm.fields.some(f => f.type === "availability") && (
                  <div className="mb-4 p-3 bg-light rounded d-flex align-items-center justify-content-between">
                    <div>
                      <strong>Your availability is saved</strong>
                      <div className="text-muted small">You can add these sessions to your calendar.</div>
                    </div>
                    <Button variant="outline-primary" size="sm" onClick={downloadCalendar} disabled={calendarLoading}>
                      <FaCalendarAlt className="me-2" />
                      {calendarLoading ? "Generating…" : "Add to Calendar"}
                    </Button>
                  </div>
                )}
                <div className="project-form-fields">
                  {displayFieldsFor(activeForm).map((field, fieldIndex) => (
                    <fieldset key={field._id} className="project-form-field">
                      <legend>
                        <span>{fieldIndex + 1}.</span>{" "}
                        {field.type === "availability" ? "Choose your sessions" : field.label}
                        {(field.type === "availability"
                          ? activeForm.fields.some((item) => item.type === "availability" && item.required)
                          : field.required) && <em>Required</em>}
                      </legend>
                      {field.helpText && field.type !== "information" && <p>{field.helpText}</p>}

                      {field.type === "information" && (
                        <div className="project-form-information"><LinkifiedText text={field.helpText || field.label} /></div>
                      )}

                      {field.type === "short_text" && (
                        <Form.Control value={answers[field._id] || ""} onChange={(event) => setAnswer(field._id, event.target.value)} required={field.required} />
                      )}
                      {field.type === "long_text" && (
                        <Form.Control as="textarea" rows={4} value={answers[field._id] || ""} onChange={(event) => setAnswer(field._id, event.target.value)} required={field.required} />
                      )}
                      {field.type === "single_choice" && (
                        <div className="project-form-options">
                          {field.options.map((option) => (
                            <Form.Check key={option._id || option.value} type="radio" name={`field-${field._id}`} id={`option-${field._id}-${option.value}`} label={option.label} value={option.value} checked={answers[field._id] === option.value} onChange={() => setAnswer(field._id, option.value)} required={field.required} />
                          ))}
                        </div>
                      )}
                      {field.type === "multi_choice" && (
                        <div className="project-form-options">
                          {field.options.map((option) => (
                            <Form.Check key={option._id || option.value} type="checkbox" id={`option-${field._id}-${option.value}`} label={option.label} checked={(answers[field._id] || []).includes(option.value)} onChange={() => toggleArrayAnswer(field._id, option.value)} />
                          ))}
                        </div>
                      )}
                      {field.type === "availability" && (
                        <AvailabilityPicker
                          fields={activeForm.fields}
                          answers={answers}
                          onToggle={toggleArrayAnswer}
                        />
                      )}
                    </fieldset>
                  ))}
                </div>
              </Form>
            </Modal.Body>
            <Modal.Footer>
              <Button type="button" variant="outline-secondary" onClick={() => setActiveForm(null)} disabled={saving}>Cancel</Button>
              <Button type="submit" form="availability-form" disabled={saving}>{saving ? "Saving…" : "Save availability"}</Button>
            </Modal.Footer>
          </>
        )}
      </Modal>
    </section>
  );
};

export default ProjectFormsPanel;
